import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { 
  convertToRiskProfile, 
  convertToAccountOwner, 
  convertToCategories,
  EnhancedRoadmapDto,
  Banner
} from '@cloudclevr/components';
import {  hasCustomerVisibleCosts } from '@hooks/roadmaps/types';
import { useRoadmapQuery, useRoadmapRequestArrangeCallbackMutation} from '@hooks/roadmaps';
import CategoriesList from './CategoriesList';
import useAuthStore from '@store/authStore';
import { UserRoles } from '@hooks/users/types';
import { WelcomeModal } from '@components/partials/WelcomeModal';
import AccountOwnerCard from './AccountOwnerCard';
import RoadmapHeader from './RoadmapHeader';
import { usePageHeader } from '../../context/PageHeaderContext';


export const Roadmap: React.FC = () => {
  const { roadmapId } = useParams<{ roadmapId: string }>();
  const query = useRoadmapQuery(roadmapId);  
  const { instance } = useMsal();
  const userRole = useAuthStore.getState().role || UserRoles.STANDARD;
  const { setCustomTitle } = usePageHeader();

  const [accountOwnerImageUrl, setAccountOwnerImageUrl] = useState<string | undefined>(undefined);
  const [photoAttempted, setPhotoAttempted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const arrangeCallbackMutation = useRoadmapRequestArrangeCallbackMutation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

   // Set the page title when roadmap data loads
  useEffect(() => {
    if (query.data) {
      const roadmapData = query.data as unknown as EnhancedRoadmapDto;
      setCustomTitle(roadmapData.name);
    }

    // Cleanup: reset title when component unmounts
    return () => {
      setCustomTitle(null);
    };
  }, [query.data, setCustomTitle]);


  // Fetch account owner photo from Microsoft Graph
  useEffect(() => {
    const attemptPhotoFetch = async () => {
      // Only try once per email address
      if (!query.data?.accountOwnerEmailAddress || photoAttempted) {
        return;
      }

      setPhotoAttempted(true);

      try {
        const account = instance.getAllAccounts()[0];
        if (!account) {
          console.log('No MSAL account found');
          return;
        }

        // Use .default scope to get ALL granted permissions
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ['https://graph.microsoft.com/.default'],
          account,
        });

        // Check if this is the current user's email
        const currentUserEmail = account.username;
        const isCurrentUser = currentUserEmail.toLowerCase() === query.data.accountOwnerEmailAddress.toLowerCase();

        // Use /me endpoint ONLY for current user, /users for everyone else
        const photoUrl = isCurrentUser 
          ? 'https://graph.microsoft.com/v1.0/me/photo/$value'
          : `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(query.data.accountOwnerEmailAddress)}/photo/$value`;

        const response = await fetch(photoUrl, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
        });

        if (response.ok) {
          const blob = await response.blob();
          if (blob.size > 0) {
            const imageUrl = URL.createObjectURL(blob);
            setAccountOwnerImageUrl(imageUrl);
          }
        } else {
          console.log(`📷 Photo not available for ${query.data.accountOwnerEmailAddress}: ${response.status}`);
        }
      } catch (error: unknown) {
        console.log('📷 Photo fetch error:', error);
      }
    };

    if (query.data && !photoAttempted) {
      attemptPhotoFetch();
    }

    // Cleanup
    return () => {
      if (accountOwnerImageUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(accountOwnerImageUrl);
      }
    };
  }, [query.data?.accountOwnerEmailAddress, instance, photoAttempted, accountOwnerImageUrl]);

  // Reset photo state when email changes
  useEffect(() => {
    setPhotoAttempted(false);
    setAccountOwnerImageUrl(undefined);
  }, [query.data?.accountOwnerEmailAddress]);

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

 const handleArrangeCallback = async (notes?: string) => {
    try {
      await arrangeCallbackMutation.mutateAsync(notes);
    } catch (error) {
      console.error('Error arranging callback:', error);
    }
  };
  

  // Loading state
  if (query.isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading roadmap...</div>
      </div>
    );
  }

  // Error state
  if (query.isError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-red-600">
          Error loading roadmap: {query.error instanceof Error ? query.error.message : 'Unknown error'}
        </div>
      </div>
    );
  }

  // No data state
  if (!query.data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">No roadmap data available</div>
      </div>
    );
  }

  // Type assertion to work with the actual API response type
  const roadmapData = query.data as unknown as EnhancedRoadmapDto;
  
  // Convert API data to component props using helper functions
  const riskProfile = convertToRiskProfile(roadmapData.severity);
  
  // Create account owner - only include photo if we successfully got one
  const accountOwner = convertToAccountOwner({ 
    ...roadmapData, 
    accountOwnerImageUrl: accountOwnerImageUrl // Will be undefined if no photo
  });
  
  const categoriesData = convertToCategories(roadmapData.categories);
  
  // Map user role to expected format
  const mappedUserRole: 'Admin' | 'Platform' | 'Super-admin' = 
    userRole === UserRoles.SUPERADMIN ? 'Super-admin' : 
    userRole === UserRoles.ADMIN ? 'Admin' : 
    'Platform';

  const handleViewAllRecommendations = () => {
    console.log('View all recommendations clicked');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Roadmap Info Modal */}      
      <WelcomeModal data-testid="roadmap-info-modal"
        modalKey="RoadmapInfoModal"                
        btnText="Got it"
        title="Plot a course for your tech"
        content={          
            <div className="space-y-4 mb-8 text-gray-700 leading-relaxed">
              <p>
                Your technology roadmap is a living document that breaks down risks and 
                recommendations across key areas. Nothing&apos;s set in stone – we&apos;ll work with 
                you to tailor your roadmap, based on your objectives and budget.
              </p>
              
              <p>
                We take a cloud-first approach, so that all our clients can continue to do 
                business as usual, no matter what challenges today&apos;s remote-first world 
                presents.
              </p>
              
              <p>
                Microsoft&apos;s a large part of that; our expertise in what Microsoft can do, 
                across reliability, security and compliance, leaves you free to focus on the 
                thing that matters most: growing your business.
              </p>
          </div>          
        }
      />
      <div className="px-6 py-0 pt-0 mt-0 ">
        {/* Roadmap Header */}
        <RoadmapHeader  data-testid="roadmap-header"
          customerName={roadmapData.customerName} 
          roadmapId={roadmapData.id}   
          caption={roadmapData.name}   
          riskProfile={riskProfile}          
          showCostBreakdown={hasCustomerVisibleCosts(roadmapData.recommendations || [])}
          onViewAllRecommendations={handleViewAllRecommendations}          
        />
      </div>
      
      {/* Status Banner */}
      <div className="px-6" data-testid="status-banner">
        {/* <Banner
          roadmapId={roadmapData.id}
          status={roadmapData.status}
          customerName={roadmapData.customerName}
          categories={roadmapData.categories}
          modifiedDate={roadmapData.modifiedDate}
        /> */}
      </div>
      
      {/* Categories List */}
      <div className="px-6 py-6" data-testid="categories-list">
        <CategoriesList
          categories={categoriesData}
          userRole={mappedUserRole}
          customerId={roadmapData.customerId} 
          customerName={roadmapData.customerName}
        /> 
      </div>

      {/* Account Owner Card */}
      <div className="px-6 pb-6" data-testid="account-owner-card">
        <AccountOwnerCard
          accountOwnerName={roadmapData.accountOwnerName || 'Unknown'}
          accountOwnerEmail={roadmapData.accountOwnerEmailAddress}
          accountOwnerImageUrl={accountOwnerImageUrl}
          onArrangeCallback={handleArrangeCallback}
        />
      </div>
    </div>

    
  );
};

export default Roadmap;